import React, { useState, useEffect } from "react";
import Link from "../TrailingSlashLink";
import Logo from "../Logo";
import { MapPin, Mail, Phone } from "lucide-react";
import {
  fetchAgencySuggestions,
  fetchStatesByZone,
  fetchSubcategoryMaster,
} from "../../services/tenders";
import { useAuth } from "../../context/AuthContext";
import { shouldShowTenderBoqLeadNavAndFooter } from "../../utils/loginPlansAccess";

const slugifyTenderBiddingValue = (value) =>
  String(value || "")
    .trim()
    .replace(/&/g, " and ")
    .replace(/\(([^)]+)\)/g, " $1 ")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

const buildTenderBiddingServicePath = (type, value) => {
  const normalizedType = String(type || "").toLowerCase();
  const slug = slugifyTenderBiddingValue(value);
  const allowedTypes = new Set(["state", "category", "department"]);

  if (!allowedTypes.has(normalizedType) || !slug) return "/search-list";
  return `/tender-bidding-service/${normalizedType}/${slug}/`;
};
const Footer = () => {
  const { isAuthenticated, user, isLoading: authLoading } = useAuth();
  const showBoqLeadFooterLink = shouldShowTenderBoqLeadNavAndFooter(
    isAuthenticated,
    user,
    authLoading
  );

  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoryLetter, setCategoryLetter] = useState("");
  const [authorityLetter, setAuthorityLetter] = useState("");
  const [authorities, setAuthorities] = useState([]);
  const [authoritiesLoading, setAuthoritiesLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setStatesLoading(true);
        const data = await fetchStatesByZone(null);
        if (!isMounted) return;
        setStates(data.map((s) => s.label || s.value || s).filter(Boolean));
      } catch {
        if (isMounted) setStates([]);
      } finally {
        if (isMounted) setStatesLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setCategoriesLoading(true);
        const res = await fetchSubcategoryMaster(1, 700);
        if (!isMounted) return;
        const list = (res?.data || [])
          .map((item) => (typeof item === "string" ? item : item?.subcategory))
          .map((name) => String(name || "").trim())
          .filter(Boolean);
        setCategories(list);
      } catch {
        if (isMounted) setCategories([]);
      } finally {
        if (isMounted) setCategoriesLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setAuthoritiesLoading(true);
        const list = await fetchAgencySuggestions("", 10000, { allowEmptyPrefix: true });
        if (!isMounted) return;
        const names = (Array.isArray(list) ? list : [])
          .map((item) => (typeof item === "string" ? item : item?.label ?? item?.value ?? ""))
          .map((name) => String(name || "").trim())
          .filter(Boolean);
        setAuthorities(names);
      } catch {
        if (isMounted) setAuthorities([]);
      } finally {
        if (isMounted) setAuthoritiesLoading(false);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  const SeoLinks = ({ items, toFn }) => (
    <div className="ft-seo-links">
      {items.map((item, i) => (
        <React.Fragment key={i}>
          <a href={toFn(item)} className="ft-seo-link">{item} Tenders</a>
          {i < items.length - 1 && <span className="ft-sep">|</span>}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <>
      <style>{`
        .footer { font-family: inherit; }

        /* ── SEO 4-column section ─────────────────────────── */
        .ft-seo {
          padding: 0 0 36px 0;
          border-bottom: 1px solid rgba(255,255,255,0.08);
          user-select: none;
          -webkit-user-select: none;
        }
        .ft-seo-row { row-gap: 28px; }
        .ft-seo-heading {
          font-size: 0.6875rem !important;
          font-weight: 700 !important;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgb(255 255 255 / 78%) !important;
          margin-bottom: 12px !important;
          margin-top: 0 !important;
        }
        .ft-seo-links {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          line-height: 1.72;
        }
        .ft-seo-link {
          font-size: 0.8125rem !important;
          color: rgb(221 239 255 / 68%) !important;
          white-space: nowrap;
          transition: color 0.18s;
          text-decoration: none !important;
        }
        .ft-seo-link:hover {
          color: #1fa0f9 !important;
          text-decoration: none !important;
        }
        .ft-sep {
          color: rgba(255,255,255,0.15) !important;
          font-size: 0.75rem !important;
          margin: 0 5px;
          user-select: none;
        }
        .ft-loading {
          font-size: 0.8125rem;
          color: rgba(255,255,255,0.28) !important;
          margin: 0;
        }
        .ft-az-row {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-bottom: 12px;
        }
        .ft-az-btn {
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.55);
          border-radius: 4px;
          padding: 2px 7px;
          font-size: 0.6875rem;
          font-weight: 700;
          cursor: pointer;
          line-height: 1.6;
          transition: background 0.15s, color 0.15s, border-color 0.15s;
        }
        .ft-az-btn:hover {
          background: rgba(31,160,249,0.18);
          color: #1fa0f9;
          border-color: rgba(31,160,249,0.35);
        }
        .ft-az-btn.active {
          background: #fff;
          color: #111;
          border-color: #fff;
        }

        /* ── Main grid ────────────────────────────────────── */
        .ft-main { padding: 52px 0 44px; }
        .ft-grid-row { row-gap: 36px; }

        /* Brand */
        .ft-logo-wrap { margin-bottom: 14px; }
        .ft-tagline {
          font-size: 0.9375rem !important;
          font-weight: 700 !important;
          color: #fff !important;
          margin-bottom: 8px !important;
          letter-spacing: -0.01em;
        }
        .ft-desc {
          font-size: 0.8125rem !important;
          color: rgb(151 167 180) !important;
          line-height: 1.72 !important;
          margin-bottom: 22px !important;
          max-width: 320px;
        }

        /* Contact */
        .ft-contact-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 22px; }
        .ft-contact-item {
          display: flex;
          align-items: flex-start;
          gap: 9px;
          font-size: 0.8125rem;
          color: rgb(221 239 255 / 68%) !important;
          line-height: 1.55;
        }
        .ft-contact-icon { color: #1fa0f9; flex-shrink: 0; margin-top: 1px; }
        .ft-contact-link {
          color: rgb(221 239 255 / 68%) !important;
          transition: color 0.18s;
          text-decoration: none !important;
        }
        .ft-contact-link:hover { color: #1fa0f9 !important; text-decoration: none !important; }
        .ft-contact-sep { color: rgb(255 255 255 / 20%); margin: 0 3px; }

        /* Social */
        .ft-social { display: flex; gap: 7px; flex-wrap: wrap; }
        .ft-social-btn {
          width: 34px; height: 34px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.1);
          background: rgba(255,255,255,0.05);
          display: inline-flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,0.58) !important;
          font-size: 0.875rem;
          transition: background 0.18s, border-color 0.18s, color 0.18s;
          text-decoration: none !important; flex-shrink: 0;
        }
        .ft-social-btn:hover {
          background: rgba(0,176,116,0.14);
          border-color: rgba(0,176,116,0.35);
          color: #1fa0f9 !important;
          text-decoration: none !important;
        }

        /* Nav columns */
        .ft-col-hd {
          font-size: 0.6875rem !important;
          font-weight: 700 !important;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: rgb(255 255 255 / 78%) !important;
          margin-bottom: 18px !important;
          margin-top: 0 !important;
        }
        .ft-links { list-style: none; padding: 0; margin: 0; }
        .ft-links li { margin-bottom: 11px; }
        .ft-links li:last-child { margin-bottom: 0; }
        .ft-link {
          font-size: 0.875rem !important;
          color: rgb(221 239 255 / 68%) !important;
          line-height: 1.4;
          transition: color 0.18s;
          text-decoration: none !important;
          font-weight: 300;
        }
        .ft-link:hover { color: #1fa0f9 !important; text-decoration: none !important; }

        /* Bottom */
        .ft-bottom { border-top: 1px solid rgba(255,255,255,0.08); padding: 18px 0; }
        .ft-copyright {
          font-size: 0.8125rem !important;
          color: rgba(226,226,226,0.38) !important;
          text-align: center; margin: 0 !important;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .ft-main { padding: 40px 0 36px; }
          .ft-desc { max-width: 100%; }
          .ft-grid-row { row-gap: 32px; }
          .ft-seo { padding: 28px 0; }
        }
        @media (max-width: 767px) {
          .ft-main { padding: 32px 0 28px; }
          .ft-grid-row { row-gap: 28px; }
          .ft-seo { padding: 24px 0; }
          .ft-seo-row { row-gap: 24px; }
        }
        @media (max-width: 575px) {
          .ft-main { padding: 28px 0 24px; }
          .ft-col-hd { margin-bottom: 14px !important; }
          .ft-links li { margin-bottom: 9px; }
        }

        .ft-seo-row {
            row-gap: 28px;
            padding-top: 45px;
            border-top: 1px solid rgb(239 247 253 / 16%);
            display: flex;
            flex-direction: column;
        }
        .ft-seo-row > div {
            max-height: 300px;
            overflow: auto;
        }
        .ft-authorities {
          // height: 300px;
          overflow: hidden !important;
          display: flex;
          flex-direction: column;
        }
        .ft-authorities-scroll {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 6px;
        }

      `}</style>

      <footer className="footer bg-ebony-clay dark-mode-texts">

      

        {/* ── Main footer grid ─────────────────────────────── */}
        <div className="ft-main">
          <div className="container">
            <div className="row ft-grid-row">

              {/* Brand + Contact + Social */}
              <div className="col-lg-4 col-12">
                <div className="ft-logo-wrap"><Logo white /></div>
                <p className="ft-tagline">Empowering Bids With Intelligence</p>
                <p className="ft-desc">
                  India's leading tender intelligence platform — AI‑powered discovery,
                  bid scoring, document analysis, and expert consultancy for MSMEs,
                  contractors, and suppliers.
                </p>
                
                <div className="ft-social">
                  <a href="https://www.facebook.com/tenderapex/" target="_blank" rel="noopener noreferrer" className="ft-social-btn" aria-label="Facebook">
                    <i className="fab fa-facebook-f" />
                  </a>
                  <a href="https://www.linkedin.com/in/brijesh-dave-b591033ba/" target="_blank" rel="noopener noreferrer" className="ft-social-btn" aria-label="LinkedIn">
                    <i className="fab fa-linkedin-in" />
                  </a>
                  <a href="https://x.com/tenderapex" target="_blank" rel="noopener noreferrer" className="ft-social-btn" aria-label="X (Twitter)">
                    <span style={{ fontWeight: 800, fontSize: 13, lineHeight: 1 }}>𝕏</span>
                  </a>
                  <a href="https://www.instagram.com/tenderapex/" target="_blank" rel="noopener noreferrer" className="ft-social-btn" aria-label="Instagram">
                    <i className="fab fa-instagram" />
                  </a>
                  <a href="https://www.youtube.com/@TenderApex" target="_blank" rel="noopener noreferrer" className="ft-social-btn" aria-label="YouTube">
                    <i className="fab fa-youtube" />
                  </a>
                </div>
              </div>

              {/* Services */}
              <div className="col-lg-2 col-6">
                <h5 className="ft-col-hd">Services</h5>
                <ul className="ft-links">
                  <li><Link to="/boq-leads"           className="ft-link">BOQ Leads</Link></li>
                  <li><Link to="/bid-intelligence"    className="ft-link">Bid Intelligence</Link></li>
                  <li><Link to="/bidding-consultancy" className="ft-link">Bidding Consultancy</Link></li>
                  <li><Link to="/gem-consultancy"     className="ft-link">GeM Consultancy</Link></li>
                  <li><Link to="/compliance-services" className="ft-link">Compliance Services</Link></li>
                </ul>
              </div>

            
              {/* Company */}
              <div className="col-lg-2 col-6 offset-lg-0">
                <h5 className="ft-col-hd">Company</h5>
                <ul className="ft-links">
                  <li><Link to="/about-us"      className="ft-link">About Us</Link></li>
                  <li><a    href="/contact"     className="ft-link">Contact</a></li>
                  <li><Link to="/log-complaint" className="ft-link">Log Complaint</Link></li>
                  <li><Link to="/faq"           className="ft-link">FAQs</Link></li>
                  <li><Link to="/careers"       className="ft-link">Careers</Link></li>
                  <li><Link to="/terms"         className="ft-link">Terms of Service</Link></li>
                </ul>
              </div>

              {/* Quick Contact */}
              <div className="col-lg-3 col-6 offset-lg-0">
                <h5 className="ft-col-hd">Quick Contact</h5>

                <div className="ft-contact-list">
                  <div className="ft-contact-item">
                    <MapPin size={13} className="ft-contact-icon" />
                    <span>
                      707, Iconic Shyamal, Nr Shyamal Cross Road,
                      132 Feet Ring Road, Satellite, Ahmedabad – 380015
                    </span>
                  </div>
                  <div className="ft-contact-item">
                    <Mail size={13} className="ft-contact-icon" />
                    <div>
                      <a href="mailto:sales@tenderapex.com" className="ft-contact-link">sales@tenderapex.com</a>
                      <span className="ft-contact-sep">·</span>
                      <a href="mailto:care@tenderapex.com" className="ft-contact-link">care@tenderapex.com</a>
                    </div>
                  </div>
                  <div className="ft-contact-item">
                    <Phone size={13} className="ft-contact-icon" />
                    <div>
                      <a href="tel:+919081155188" className="ft-contact-link">+91 9081-155-188</a>
                      <span className="ft-contact-sep">·</span>
                      <a href="tel:+919925155188" className="ft-contact-link">+91 9925-155-188</a>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>


  {/* ── SEO 4-column grid (non-authenticated only) ───── */}
        {!isAuthenticated && (
          <div className="ft-seo">
            <div className="container">
              <div className="ft-seo-row">

                {/* Tender by States */}
                <div className="">
                  <h6 className="ft-seo-heading">Tender by States</h6>
                  {statesLoading
                    ? <p className="ft-loading">Loading…</p>
                    : <SeoLinks items={states} toFn={(s) => buildTenderBiddingServicePath("state", s)} />
                  }
                </div>

                {/* Tender by Sub-Categories */}
                <div className="">
                  <h6 className="ft-seo-heading">Tender by Sub-Categories</h6>
                  {categoriesLoading ? (
                    <p className="ft-loading">Loading…</p>
                  ) : (() => {
                    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
                    const available = new Set(categories.map(c => c[0]?.toUpperCase()).filter(Boolean));
                    const filtered = categoryLetter
                      ? categories.filter(c => c[0]?.toUpperCase() === categoryLetter)
                      : [];
                    return (
                      <>
                        <div className="ft-az-row">
                          {alphabet.map(letter => (
                            <button
                              key={letter}
                              className={`ft-az-btn${categoryLetter === letter ? " active" : ""}${!available.has(letter) ? " disabled" : ""}`}
                              style={!available.has(letter) ? { opacity: 0.3, cursor: "default" } : {}}
                              onClick={() => available.has(letter) && setCategoryLetter(prev => prev === letter ? "" : letter)}
                            >
                              {letter}
                            </button>
                          ))}
                        </div>
                        {filtered.length > 0 && (
                          <SeoLinks items={filtered} toFn={(c) => buildTenderBiddingServicePath("category", c)} />
                        )}
                        {categoryLetter && filtered.length === 0 && (
                          <p className="ft-loading">No categories found for "{categoryLetter}"</p>
                        )}
                        {!categoryLetter && (
                          <p className="ft-loading" style={{ fontSize: "0.75rem" }}>Select a letter to browse categories</p>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Tender by Authorities */}
                <div className="ft-authorities">
                  <h6 className="ft-seo-heading">Tender by Authorities</h6>
                  {authoritiesLoading ? (
                    <p className="ft-loading">Loading…</p>
                  ) : (() => {
                    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
                    const available = new Set(authorities.map(a => a[0]?.toUpperCase()).filter(Boolean));
                    const filtered = authorityLetter
                      ? authorities.filter(a => a[0]?.toUpperCase() === authorityLetter)
                      : [];
                    return (
                      <>
                        <div className="ft-az-row">
                          {alphabet.map(letter => (
                            <button
                              key={letter}
                              className={`ft-az-btn${authorityLetter === letter ? " active" : ""}${!available.has(letter) ? " disabled" : ""}`}
                              style={!available.has(letter) ? { opacity: 0.3, cursor: "default" } : {}}
                              onClick={() => available.has(letter) && setAuthorityLetter(prev => prev === letter ? "" : letter)}
                            >
                              {letter}
                            </button>
                          ))}
                        </div>
                        <div className="ft-authorities-scroll">
                          {filtered.length > 0 && (
                            <SeoLinks items={filtered} toFn={(a) => buildTenderBiddingServicePath("department", a)} />
                          )}
                          {authorityLetter && filtered.length === 0 && (
                            <p className="ft-loading">No authorities found for "{authorityLetter}"</p>
                          )}
                          {!authorityLetter && (
                            <p className="ft-loading" style={{ fontSize: "0.75rem" }}>Select a letter to browse authorities</p>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Tender by Procurement */}
                {/* <div className="">
                  <h6 className="ft-seo-heading">Tender by Procurement</h6>
                  <div className="ft-seo-links">
                    <Link to="/procurement/sell" className="ft-seo-link">Sell Tenders</Link>
                    <span className="ft-sep">|</span>
                    <Link to="https://bidplus.gem.gov.in" target="_blank" className="ft-seo-link">GEM Tenders</Link>
                    <span className="ft-sep">|</span>
                    <Link to="https://tender.nprocure.com" target="_blank" className="ft-seo-link">NPROCURE Tenders</Link>
                    <span className="ft-sep">|</span>
                    <Link to="https://ireps.gov.in" target="_blank" className="ft-seo-link">IREPS Tenders</Link>
                    <span className="ft-sep">|</span>
                    <Link to="/procurement/eprocure" className="ft-seo-link">CPPP</Link>
                  </div>
                </div> */}

              </div>
            </div>
          </div>
        )}

        {/* ── Bottom bar ───────────────────────────────────── */}
        <div className="ft-bottom">
          <div className="container">
            <p className="ft-copyright">
              © 2026 Vedaks Info Technologies Private Limited. All Rights Reserved.
            </p>
          </div>
        </div>

      </footer>
    </>
  );
};

export default Footer;
